import { useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { useConnection, useSignMessage } from 'wagmi'
import type { ApiKeyCreated } from '@algoboo/schema/api'
import { AddressLink, Button, Card, CopyValue, CreditMeter, EmptyState, Eyebrow, Field, Icon, Input, Pill, Skeleton, formatCount, fullTime, timeAgo, type IconName } from '@algoboo/ui'
import { errorCopy, invalidate, isApiError, myApiKeysQuery, myWalletsQuery, request, useApiMutation, useMyApiKeys, useMyWallets, useUnreadCount } from '../lib/api'
import { isSafeAvatarUrl } from '../lib/identity'
import { UserAvatar } from '../components/UserAvatar'
import { usePageMeta } from '../lib/meta'
import { buildSiweMessage } from '../lib/siwe'
import { PageHeader } from '../components/Shell'
import { SignInPrompt, useSession } from '../components/session'
import { prefetchSignedIn } from '../lib/route-data'
import { attempt } from '../lib/attempt'

export const Route = createFileRoute('/me/')({
  loader: ({ context: { queryClient: qc } }) => prefetchSignedIn(qc, () => [qc.ensureQueryData(myWalletsQuery()), qc.ensureQueryData(myApiKeysQuery())]),
  component: MePage,
})

function MePage() {
  usePageMeta('Profile')
  const { me, loading, signOut } = useSession()
  if (loading) return <Skeleton className="h-64 rounded-xl" />
  if (!me) return <SignInPrompt title="Your profile" body="Handle, linked wallets, API keys and plan. Sign in with your wallet to manage them." />
  return (
    <>
      <PageHeader
        eyebrow="Account"
        title={me.user.displayName}
        lede={
          <span className="font-mono text-[13px]">
            {me.user.handle ? `@${me.user.handle} · ` : ''}member since {me.user.createdAt.slice(0, 10)}
          </span>
        }
        actions={
          <Button variant="ghost" icon="logout" onClick={() => void signOut()}>
            Sign out
          </Button>
        }
      />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ProfileCard />
        <PlacesCard />
        <WalletsCard />
        <KeysCard />
        <PlanCreditsCard />
      </div>
    </>
  )
}

function PlanCreditsCard() {
  const { me } = useSession()
  return (
    <Card className="p-5 lg:col-span-2" data-testid="me-billing">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-display text-[18px] font-semibold">Compute credits</h2>
        <Link to="/portfolio/billing" className="text-[13px] font-medium text-accent hover:underline">
          Manage billing
        </Link>
      </div>
      <CreditMeter availableMicro={me!.credits.availableMicro} heldMicro={me!.credits.heldMicro} settledMicro={me!.credits.settledMicro} />
      <p className="mt-3 text-[12.5px] text-ink-3">
        Top up compute credits in USDG from your wallet on the <Link to="/portfolio/billing" className="text-accent hover:underline">billing page</Link>.
      </p>
    </Card>
  )
}

function ProfileCard() {
  const { me } = useSession()
  const u = me!.user
  const [f, setF] = useState({ handle: u.handle ?? '', displayName: u.displayName, avatarUrl: u.avatarUrl ?? '' })
  const patch = useApiMutation('patchMe', { invalidate: () => [['me']] })
  const handleOk = /^[a-z0-9_]{3,24}$/.test(f.handle)
  const avatarOk = f.avatarUrl.trim() === '' || isSafeAvatarUrl(f.avatarUrl.trim())
  const valid = handleOk && f.displayName.trim().length > 0 && avatarOk
  const preview = { handle: handleOk ? f.handle : u.handle, primaryAddress: u.primaryAddress, avatarUrl: avatarOk && f.avatarUrl.trim() ? f.avatarUrl.trim() : null }
  return (
    <Card className="p-5">
      <div className="flex items-center gap-4">
        <UserAvatar user={preview} size={72} framing="full" />
        <div className="min-w-0">
          <h2 className="font-display text-[18px] font-semibold">Profile</h2>
          <p className="text-[12.5px] text-ink-3">Your handle appears as creator on agents you make.</p>
          {u.handle && (
            <Link to="/u/$handle" params={{ handle: u.handle }} className="text-[12.5px] font-medium text-accent hover:underline">
              View your creator page
            </Link>
          )}
        </div>
      </div>
      <form
        className="mt-4 grid gap-3 sm:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault()
          const avatarUrl = f.avatarUrl.trim() === '' ? null : f.avatarUrl.trim()
          patch.mutate({ body: { handle: f.handle, displayName: f.displayName, ...(avatarUrl !== (u.avatarUrl ?? null) ? { avatarUrl } : {}) } })
        }}
      >
        <Field htmlFor="me-handle" label="Handle" error={f.handle && !handleOk ? '3-24 characters: a-z, 0-9, _' : undefined}>
          <Input id="me-handle" prefix="@" value={f.handle} onChange={(e) => setF({ ...f, handle: e.target.value.toLowerCase() })} />
        </Field>
        <Field htmlFor="me-name" label="Display name">
          <Input id="me-name" value={f.displayName} maxLength={60} onChange={(e) => setF({ ...f, displayName: e.target.value })} />
        </Field>
        <Field
          htmlFor="me-avatar"
          label="Avatar image"
          className="sm:col-span-2"
          hint="An https image URL. Leave empty to use the mascot derived from your handle."
          error={avatarOk ? undefined : 'Use an https URL'}
        >
          <Input id="me-avatar" type="url" inputMode="url" placeholder="https://…" value={f.avatarUrl} invalid={!avatarOk} onChange={(e) => setF({ ...f, avatarUrl: e.target.value })} />
        </Field>
        <div className="flex items-center gap-3 sm:col-span-2">
          <Button type="submit" variant="primary" disabled={!valid} loading={patch.isPending}>
            Save
          </Button>
          {f.avatarUrl && (
            <Button type="button" variant="ghost" size="sm" onClick={() => setF({ ...f, avatarUrl: '' })}>
              Use my mascot
            </Button>
          )}
          {patch.isSuccess && <span className="text-[12.5px] text-gain">Saved</span>}
          {patch.error && <span className="text-[12.5px] text-danger">{isApiError(patch.error) ? errorCopy(patch.error.code).body : 'Could not save'}</span>}
        </div>
      </form>
    </Card>
  )
}

/** Where the rest of your account lives (BACKLOG C1, O3): agents, money, billing and notifications. */
function PlacesCard() {
  const { me } = useSession()
  const unread = useUnreadCount()
  const c = me!.counts
  const places: { to: '/desk' | '/portfolio' | '/portfolio/billing' | '/me/notifications'; label: string; icon: IconName; detail: string }[] = [
    { to: '/desk', label: 'My agents', icon: 'grid', detail: c.agents === 0 ? 'Create your first' : `${formatCount(c.agents)} agent${c.agents === 1 ? '' : 's'}` },
    { to: '/portfolio', label: 'Portfolio', icon: 'wallet', detail: `${formatCount(c.wallets)} trading wallet${c.wallets === 1 ? '' : 's'} · ${formatCount(c.following)} following` },
    { to: '/portfolio/billing', label: 'Billing', icon: 'coin', detail: 'Credits and payment history' },
    { to: '/me/notifications', label: 'Notifications', icon: 'bell', detail: unread.data ? (unread.data.count > 0 ? `${formatCount(unread.data.count)} unread` : 'All read') : 'Trades, refusals, expiries' },
  ]
  return (
    <Card className="p-2" data-testid="me-places">
      <ul className="divide-y divide-line">
        {places.map((p) => (
          <li key={p.to}>
            <Link to={p.to} className="group flex min-h-14 items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-sunken">
              <Icon name={p.icon} size={18} className="text-ink-3 group-hover:text-accent" />
              <span className="flex-1">
                <span className="block text-[14px] font-medium text-ink">{p.label}</span>
                <span className="block text-[12px] text-ink-3">{p.detail}</span>
              </span>
              <Icon name="chevron-right" size={15} className="text-ink-4" />
            </Link>
          </li>
        ))}
      </ul>
    </Card>
  )
}

function WalletsCard() {
  const w = useMyWallets()
  const conn = useConnection()
  const { signMessageAsync } = useSignMessage()
  const del = useApiMutation('deleteWallet', { invalidate: () => invalidate.linkedWallets() })
  const link = useApiMutation('linkWallet', { invalidate: () => invalidate.linkedWallets() })
  const [err, setErr] = useState<string | null>(null)
  const linked = (w.data ?? []).some((x) => x.address === conn.address?.toLowerCase())
  const linkConnected = async () => {
    setErr(null)
    await attempt(
      async () => {
        const addr = conn.address!
        const n = await request('siweNonce', { body: { address: addr } })
        const message = buildSiweMessage({ address: addr, nonce: n.nonce, domain: n.domain ?? window.location.host, uri: window.location.origin, issuedAt: new Date(), expiresAt: new Date(n.expiresAt), chainId: n.chainId })
        const signature = await signMessageAsync({ message, account: addr })
        await link.mutateAsync({ body: { message, signature } })
      },
      (e) => {
        setErr(isApiError(e) ? errorCopy(e.code).body : e instanceof Error ? e.message : String(e))
      },
    )
  }
  return (
    <Card className="p-5">
      <h2 className="font-display text-[18px] font-semibold">Linked wallets</h2>
      {w.isLoading ? (
        <Skeleton className="mt-3 h-16" />
      ) : (
        <ul className="mt-3 divide-y divide-line">
          {(w.data ?? []).map((x) => (
            <li key={x.id} className="flex items-center gap-3 py-2.5">
              <AddressLink value={x.address} />
              {x.isPrimary && <Pill tone="brand">primary</Pill>}
              {x.label && <span className="text-[12px] text-ink-3">{x.label}</span>}
              <span className="ml-auto font-mono text-[11px] text-ink-3" title={fullTime(x.verifiedAt)}>
                verified {timeAgo(x.verifiedAt)}
              </span>
              {!x.isPrimary && (
                <Button size="sm" variant="ghost" loading={del.isPending} onClick={() => del.mutate({ params: { id: x.id } })}>
                  Unlink
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-3 border-t border-line pt-3 text-[13px] text-ink-3">
        {conn.address && !linked ? (
          <Button size="sm" variant="soft" icon="chain" loading={link.isPending} onClick={() => void linkConnected()}>
            Link the connected wallet
          </Button>
        ) : (
          <p>To link another wallet, switch to it in your wallet extension, then come back here.</p>
        )}
        {err && <p className="mt-2 text-danger">{err}</p>}
      </div>
    </Card>
  )
}

function KeysCard() {
  const keys = useMyApiKeys()
  const [label, setLabel] = useState('')
  const [created, setCreated] = useState<ApiKeyCreated | null>(null)
  const create = useApiMutation('createMyApiKey', { invalidate: () => invalidate.apiKeys(), onSuccess: (k) => setCreated(k) })
  const revoke = useApiMutation('deleteMyApiKey', { invalidate: () => invalidate.apiKeys() })
  return (
    <Card className="p-5">
      <h2 className="font-display text-[18px] font-semibold">Personal API keys</h2>
      <p className="mt-1 text-[12.5px] text-ink-3">Read or write access to your own data from scripts. Agent keys live on each agent.</p>
      {created && (
        <div className="mt-3 rounded-lg border-2 border-warn bg-warn-soft p-3">
          <Eyebrow>Shown once</Eyebrow>
          <CopyValue value={created.secret} className="mt-1 text-[13px] text-ink" />
        </div>
      )}
      <form
        className="mt-3 flex gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          create.mutate({ body: { label, scopes: ['read'] } })
          setLabel('')
        }}
      >
        <Input aria-label="Key label" placeholder="Label, e.g. notebook" value={label} onChange={(e) => setLabel(e.target.value)} />
        <Button type="submit" icon="key" disabled={!label.trim()} loading={create.isPending}>
          Create
        </Button>
      </form>
      {create.error && <p className="mt-2 text-[12.5px] text-danger">{isApiError(create.error) ? errorCopy(create.error.code).body : 'Could not create'}</p>}
      {keys.isLoading ? (
        <Skeleton className="mt-3 h-12" />
      ) : (keys.data ?? []).length === 0 ? (
        <EmptyState compact title="No keys" />
      ) : (
        <ul className="mt-3 divide-y divide-line">
          {keys.data!.map((k) => (
            <li key={k.id} className="flex items-center gap-3 py-2 text-[13px]">
              <span className="font-semibold">{k.label}</span>
              <span className="font-mono text-ink-3">{k.prefix}…</span>
              <span className="text-ink-3">{k.scopes.join(', ')}</span>
              {k.revokedAt ? (
                <Pill tone="neutral" className="ml-auto">
                  revoked
                </Pill>
              ) : (
                <Button size="sm" variant="ghost" className="ml-auto" loading={revoke.isPending} onClick={() => revoke.mutate({ params: { id: k.id } })}>
                  Revoke
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
